import { Fragment } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { purpleColors, slateColors, fontFamily } from './ContentPages/AppLandingPage/styles';
import { TextButtonM } from './Controls/Buttons';

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem 1rem 0.5rem 1rem;
  background-color: white;
  
  @media (min-width: 768px) {
    padding: 0rem 2rem 0.75rem 2rem;
    gap: 0.75rem;
  }
`;

const NavBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  row-gap: 10px;
  align-self: stretch;
  flex-wrap: wrap;
  width: 100%;
`;

const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  cursor: ${props => props.$isActive ? 'default' : 'pointer'};
  font-family: ${fontFamily};
  font-size: 0.875rem;
  font-weight: ${props => props.$isActive ? '500' : '400'};
  color: ${props => props.$isActive ? slateColors[900] : slateColors[600]};
  transition: color 0.2s;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.$isActive ? slateColors[900] : purpleColors[600]};
  }
  
  &:focus {
    outline: none;
    color: ${purpleColors[600]};
  }
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${props => props.$isActive ? slateColors[600] : slateColors[400]};
    flex-shrink: 0;
  }
`;

const BreadcrumbSeparator = styled.span`
  display: flex;
  align-items: center;
  color: ${slateColors[400]};
  font-size: 0.875rem;
  flex-shrink: 0;
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  align-self: stretch;
`;

const Title = styled.h1`
  font-family: ${fontFamily};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin: 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 640px) {
    font-size: 1.75rem;
  }
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
    
    @media (min-width: 640px) {
      width: 1.5rem;
      height: 1.5rem;
    }
    
    @media (min-width: 768px) {
      width: 1.75rem;
      height: 1.75rem;
    }
  }
`;

const Subtitle = styled.p`
  font-family: ${fontFamily};
  font-size: 1.125rem;
  font-weight: 400;
  color: ${slateColors[600]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
    
    @media (min-width: 640px) {
      width: 1.125rem;
      height: 1.125rem;
    }
    
    @media (min-width: 768px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const SubtitleText = styled.span`
  font-family: ${fontFamily};
  font-size: 1.125rem;
  font-weight: 400;
  color: ${slateColors[600]};
  
  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ChangeButton = styled.button`
  font-family: ${fontFamily};
  font-size: 1.125rem;
  font-weight: 500;
  color: ${purpleColors[600]};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
  
  &:hover {
    color: ${purpleColors[700]};
  }
  
  &:focus {
    outline: none;
    color: ${purpleColors[700]};
  }
  
  &:active {
    color: ${purpleColors[700]};
  }
  
  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

export type BreadcrumbItemType = {
  label: string;
  path?: string;
  onClick?: () => void;
  active?: boolean;
};

type PageHeaderProps = {
  title?: string;
  subtitle?: string;
  backButtonTitle?: string;
  textButtonTitle?: string;
  textButtonOnClick?: () => void;
  showTextButton?: boolean;
  showTitleContainer?: boolean;
  breadcrumbs?: BreadcrumbItemType[];
  subtitleWithChange?: {
    text: string;
    onChangeClick: () => void;
  };
};

const PageHeader = ({
  title,
  subtitle,
  backButtonTitle,
  textButtonTitle,
  textButtonOnClick,
  showTextButton = false,
  showTitleContainer = true,
  breadcrumbs,
  subtitleWithChange,
}: PageHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultBackButtonTitle = backButtonTitle || t('common.home');

  const handleHomeClick = () => {
    navigate('/');
  };

  // Build breadcrumbs from props if breadcrumbs array is not provided (backward compatibility)
  const getBreadcrumbs = (): BreadcrumbItemType[] => {
    if (breadcrumbs && breadcrumbs.length > 0) {
      return breadcrumbs;
    }
    
    // Legacy mode: build from title and backButtonTitle
    const homeLabel = t('common.home');
    const items: BreadcrumbItemType[] = [
      {
        label: defaultBackButtonTitle,
        path: defaultBackButtonTitle === homeLabel ? "/" : undefined,
        onClick: defaultBackButtonTitle === homeLabel ? handleHomeClick : undefined,
      }
    ];
    
    if (showTitleContainer && title) {
      items.push({
        label: title,
        active: true,
      });
    }
    
    return items;
  };

  const breadcrumbItems = getBreadcrumbs();
  
  // Ensure we always have at least one breadcrumb (Home)
  const homeLabel = t('common.home');
  const safeBreadcrumbItems = breadcrumbItems.length > 0 
    ? breadcrumbItems 
    : [{ label: homeLabel, path: "/", onClick: handleHomeClick }];

  const handleBreadcrumbClick = (item: BreadcrumbItemType) => {
    if (item.active) return;
    
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Get the active title - prioritize provided title prop over breadcrumb label
  const activeTitle = title || (safeBreadcrumbItems.length > 0 
    ? safeBreadcrumbItems[safeBreadcrumbItems.length - 1]?.label || ''
    : '');

  // Replace arrow symbol with ArrowRight icon
  const renderTitle = (titleText: string) => {
    const parts = titleText.split(' ➜ ');
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <ArrowRight />
          {parts[1]}
        </>
      );
    }
    return titleText;
  };

  // Replace arrow symbol with ArrowRight icon in subtitle
  const renderSubtitle = (subtitleText: string) => {
    const parts = subtitleText.split(' ➜ ');
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <ArrowRight />
          {parts[1]}
        </>
      );
    }
    return subtitleText;
  };

  return (
    <Header>
      <NavBar>
        <BreadcrumbContainer>
          {safeBreadcrumbItems.map((item, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <BreadcrumbSeparator>
                  <ChevronRight />
                </BreadcrumbSeparator>
              )}
              <BreadcrumbItem
                onClick={() => handleBreadcrumbClick(item)}
                $isActive={item.active || index === safeBreadcrumbItems.length - 1}
              >
                {index === 0 && item.label === homeLabel && <Home />}
                {item.label}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbContainer>
        {showTextButton && (
          <TextButtonM onClick={textButtonOnClick}>
            {textButtonTitle}
          </TextButtonM>
        )}
      </NavBar>
      {showTitleContainer && (
        <TitleContainer>
          <Title>{renderTitle(activeTitle)}</Title>
          {subtitleWithChange ? (
            <SubtitleRow>
              <SubtitleText>{subtitleWithChange.text}</SubtitleText>
              <ChangeButton onClick={subtitleWithChange.onChangeClick}>
                {t('common.change')}
              </ChangeButton>
            </SubtitleRow>
          ) : subtitle ? (
            <Subtitle>{renderSubtitle(subtitle)}</Subtitle>
          ) : null}
        </TitleContainer>
      )}
    </Header>
  );
};

export default PageHeader;
